import type { TelegramLink } from "@cloudserver/contracts";
import { ExternalLink } from "lucide-react";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { dateTime } from "../../ui/format.js";
import { Dialog } from "../../ui/overlay.js";
import { Button, Spinner } from "../../ui/primitives.js";
import { CommandBlock } from "../server/autopilot/command.js";

/**
 * A one-time deep link to a bot (docs/alerts.md "Linking"): open it in
 * Telegram, or send the bot the command by hand. The page notices the link
 * by itself once the bot got it.
 */
export function TelegramLinkDialog({
  title,
  steps,
  link,
  error,
  onClose,
}: {
  title: string;
  steps: string[];
  link: TelegramLink | undefined;
  error: ReactNode;
  onClose: () => void;
}) {
  const { t, i18n } = useTranslation();
  return (
    <Dialog
      open
      onOpenChange={(open) => !open && onClose()}
      title={title}
      footer={<Button onClick={onClose}>{t("alerts.link.done")}</Button>}
    >
      <div className="flex flex-col gap-4">
        <ol className="flex list-decimal flex-col gap-1 pl-5 text-sm text-muted">
          {steps.map((step) => (
            <li key={step}>{step}</li>
          ))}
        </ol>
        {error}
        {!link && !error && <Spinner label={t("common.loading")} />}
        {link && (
          <>
            <a
              href={link.url}
              target="_blank"
              rel="noreferrer noopener"
              className="inline-flex min-h-10 items-center justify-center gap-2 self-start rounded-lg bg-accent px-4 text-sm font-medium text-accent-fg hover:bg-accent-strong"
            >
              <ExternalLink className="size-4" aria-hidden />
              {t("alerts.link.open", { bot: link.botUsername })}
            </a>
            <div className="flex flex-col gap-1.5">
              <p className="text-sm">{t("alerts.link.byHand", { bot: link.botUsername })}</p>
              <CommandBlock label={t("alerts.link.commandLabel")} command={link.command} />
            </div>
            <p className="text-xs text-muted">
              {t("alerts.link.expires", { time: dateTime(link.expiresAt, i18n.language) })}
            </p>
          </>
        )}
      </div>
    </Dialog>
  );
}
